import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Select, Spin, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";

import {
  fetchLeadWhatsAppConversation,
  markWhatsAppConversationRead,
} from "../../data/redux/services/whatsappConversationApi";
import { getErrorMessage } from "../../data/redux/utils/errorUtils";
import { humanReadableDate } from "../../../helper/dateTimeHelper";
import { WhatsAppMessageMedia } from "./WhatsAppMessageMedia";
import "./WhatsAppConversationPanel.css";

import type {
  WhatsAppConversation,
  WhatsAppFlowSubmission,
  WhatsAppMessage,
} from "./types";

interface WhatsAppConversationPanelProps {
  companyId: number | string;
  companyType: string;
  leadResponseId: number;
  height?: number | string;
}

const PAGE_SIZE = 50;
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString(undefined, TIME_FORMAT);

const formatDay = (value: string) => humanReadableDate(value, false);

const DeliveryTicks: React.FC<{ message: WhatsAppMessage }> = ({ message }) => {
  if (message.delivery_status === "failed") {
    return <i className="ti ti-alert-circle wa-bubble__ticks--failed" />;
  }
  if (message.delivery_status === "read") {
    return <i className="ti ti-checks wa-bubble__ticks--read" />;
  }
  if (message.delivery_status === "delivered") {
    return <i className="ti ti-checks" />;
  }
  return <i className="ti ti-check" />;
};

const FlowSubmissionCard: React.FC<{ flow: WhatsAppFlowSubmission }> = ({ flow }) => (
  <div className="wa-flow">
    <div className="wa-flow__title">
      <i className="ti ti-forms me-1" />
      Form submitted
    </div>
    {flow.fields.map((field) => (
      <div key={field.key} className="wa-flow__row">
        <span className="wa-flow__label">{field.label}</span>
        <span className="wa-flow__value">{field.value}</span>
      </div>
    ))}
  </div>
);

const MessageItem: React.FC<{ message: WhatsAppMessage }> = ({ message }) => {
  if (message.direction === "event") {
    const label = message.message_type === "url_click" ? "Clicked link" : "Event";
    return (
      <div className="wa-event">
        <i className="ti ti-click me-1" />
        {label}: {message.text_body || "-"} · {formatTime(message.message_timestamp)}
      </div>
    );
  }

  const isOutbound = message.direction === "outbound";
  const media = message.payload?.media;
  const flow = message.payload?.flow?.fields?.length ? message.payload.flow : null;
  const hasText = Boolean(message.text_body) && !flow;
  const placeholder = isOutbound
    ? "Message sent from provider (content not shared)"
    : `[${message.message_type}]`;

  return (
    <div className={`wa-bubble ${isOutbound ? "wa-bubble--outbound" : "wa-bubble--inbound"}`}>
      {media && <WhatsAppMessageMedia media={media} messageType={message.message_type} />}
      {flow && <FlowSubmissionCard flow={flow} />}
      {hasText && <div className={media ? "wa-bubble__caption" : undefined}>{message.text_body}</div>}
      {!hasText && !media && !flow && <span className="wa-bubble__placeholder">{placeholder}</span>}
      <div className="wa-bubble__meta">
        <span>{formatTime(message.message_timestamp)}</span>
        {isOutbound && <DeliveryTicks message={message} />}
      </div>
    </div>
  );
};

export const WhatsAppConversationPanel: React.FC<WhatsAppConversationPanelProps> = ({
  companyId,
  companyType,
  leadResponseId,
  height = 520,
}) => {
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const selectedConversation = useMemo(
    () => conversations.find((item) => item.id === selectedId) || null,
    [conversations, selectedId],
  );

  const groupedMessages = useMemo(() => {
    const groups: { day: string; items: WhatsAppMessage[] }[] = [];
    messages.forEach((message) => {
      const day = formatDay(message.message_timestamp);
      const last = groups[groups.length - 1];
      if (last && last.day === day) {
        last.items.push(message);
      } else {
        groups.push({ day, items: [message] });
      }
    });
    return groups;
  }, [messages]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    });
  };

  const loadConversation = useCallback(
    async (conversationId: number | null = null) => {
      if (!companyId || !leadResponseId) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchLeadWhatsAppConversation({
          companyId,
          companyType,
          leadResponseId,
          conversationId,
          limit: PAGE_SIZE,
        });
        setConversations(data.conversations);
        setSelectedId(data.selected_conversation_id);
        setMessages(data.messages);
        setHasMore(data.has_more);
        scrollToBottom();

        const selected = data.conversations.find(
          (item) => item.id === data.selected_conversation_id,
        );
        if (selected && selected.unread_count > 0) {
          markWhatsAppConversationRead(companyId, companyType, selected.id).catch(() => undefined);
        }
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, companyType, leadResponseId],
  );

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  const handleLoadOlder = async () => {
    if (!selectedId || !messages.length) return;
    setIsLoadingOlder(true);
    try {
      const data = await fetchLeadWhatsAppConversation({
        companyId,
        companyType,
        leadResponseId,
        conversationId: selectedId,
        beforeId: messages[0].id,
        limit: PAGE_SIZE,
      });
      setMessages((prev) => [...data.messages, ...prev]);
      setHasMore(data.has_more);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoadingOlder(false);
    }
  };

  const handleConversationChange = (conversationId: number) => {
    loadConversation(conversationId);
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="wa-conversation__empty">
          <Spin />
        </div>
      );
    }
    if (error) {
      return (
        <div className="wa-conversation__empty">
          <i className="ti ti-alert-triangle text-danger" />
          <span>{error}</span>
          <Button size="small" onClick={() => loadConversation(selectedId)}>
            Retry
          </Button>
        </div>
      );
    }
    if (!selectedConversation) {
      return (
        <div className="wa-conversation__empty">
          <i className="ti ti-brand-whatsapp" />
          <span>No WhatsApp conversation is linked to this lead yet.</span>
        </div>
      );
    }
    return (
      <div className="wa-conversation__body" ref={bodyRef}>
        {hasMore && (
          <Button
            size="small"
            className="align-self-center mb-2"
            loading={isLoadingOlder}
            onClick={handleLoadOlder}>
            Load older messages
          </Button>
        )}
        {groupedMessages.map((group) => (
          <React.Fragment key={`${group.day}-${group.items[0].id}`}>
            <div className="wa-conversation__day">{group.day}</div>
            {group.items.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="wa-conversation" style={{ height }}>
      <div className="wa-conversation__header">
        <div className="wa-conversation__contact">
          <div className="wa-conversation__avatar">
            <i className="ti ti-brand-whatsapp" />
          </div>
          <div className="text-truncate">
            <div className="fw-semibold text-truncate">
              {selectedConversation?.contact_name || selectedConversation?.contact_phone || "WhatsApp"}
            </div>
            {selectedConversation && (
              <small className="text-muted">
                +{selectedConversation.contact_phone}
                {selectedConversation.integration_name && ` · via ${selectedConversation.integration_name}`}
              </small>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          {selectedConversation && (
            <Tooltip
              title={
                selectedConversation.window_expires_at
                  ? `Customer window ${selectedConversation.is_window_open ? "open until" : "closed at"} ${humanReadableDate(selectedConversation.window_expires_at, true)}`
                  : "Customer has not messaged yet"
              }>
              <Tag color={selectedConversation.is_window_open ? "green" : "default"}>
                {selectedConversation.is_window_open ? "24h window open" : "Window closed"}
              </Tag>
            </Tooltip>
          )}
          {conversations.length > 1 && (
            <Select
              size="small"
              style={{ minWidth: 160 }}
              value={selectedId ?? undefined}
              onChange={handleConversationChange}
              options={conversations.map((item) => ({
                value: item.id,
                label: item.integration_name || `+${item.contact_phone}`,
              }))}
            />
          )}
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => loadConversation(selectedId)}
              disabled={isLoading}
            />
          </Tooltip>
        </div>
      </div>
      {renderBody()}
    </div>
  );
};
